import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, Users, BarChart, Globe, Settings, MessageSquare, CreditCard } from "lucide-react";
import dashboardImage from "@/assets/demo-dashboard-optimized.png";
import employeesImage from "@/assets/demo-employees.png";
import requestsImage from "@/assets/demo-requests.png";
import trackingImage from "@/assets/demo-tracking.png";
import countriesImage from "@/assets/demo-countries.png";
import pointsImage from "@/assets/demo-points.png";

const ProductDemoSection = () => {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      titleKey: "productDemo.slides.dashboard.title",
      descriptionKey: "productDemo.slides.dashboard.description",
      featuresKey: "productDemo.slides.dashboard.features",
      image: dashboardImage,
      icon: BarChart
    },
    {
      titleKey: "productDemo.slides.teams.title",
      descriptionKey: "productDemo.slides.teams.description",
      featuresKey: "productDemo.slides.teams.features",
      image: employeesImage,
      icon: Users
    },
    {
      titleKey: "productDemo.slides.requests.title",
      descriptionKey: "productDemo.slides.requests.description",
      featuresKey: "productDemo.slides.requests.features",
      image: requestsImage,
      icon: Settings
    },
    {
      titleKey: "productDemo.slides.tracking.title",
      descriptionKey: "productDemo.slides.tracking.description",
      featuresKey: "productDemo.slides.tracking.features",
      image: trackingImage,
      icon: Globe
    },
    {
      titleKey: "productDemo.slides.countries.title",
      descriptionKey: "productDemo.slides.countries.description",
      featuresKey: "productDemo.slides.countries.features",
      image: countriesImage,
      icon: BarChart
    },
    {
      titleKey: "productDemo.slides.points.title",
      descriptionKey: "productDemo.slides.points.description",
      featuresKey: "productDemo.slides.points.features",
      image: pointsImage,
      icon: CreditCard
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const currentFeatures = t(slides[currentSlide].featuresKey, { returnObjects: true }) as string[];

  return (
    <section className="py-24 px-4 bg-background">
      <div className="container mx-auto">
        <div className="text-center mb-16">
          <Badge variant="secondary" className="mb-4">
            <MessageSquare className="h-4 w-4 mr-2" />
            {t('productDemo.badge')}
          </Badge>
          <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
            {t('productDemo.title')}
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            {t('productDemo.subtitle')}
          </p>
        </div>

        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <div className="relative order-2 lg:order-1">
              <div className="relative bg-white rounded-2xl shadow-2xl border overflow-hidden">
                <img 
                  src={slides[currentSlide].image} 
                  alt={t(slides[currentSlide].titleKey)}
                  className="w-full h-auto transition-all duration-500"
                  width="1188"
                  height="862"
                />
              </div>
              
              {/* Navigation arrows */}
              <Button
                variant="outline"
                size="sm"
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm"
                onClick={prevSlide}
                aria-label={t('productDemo.prevSlide')}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm"
                onClick={nextSlide}
                aria-label={t('productDemo.nextSlide')}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            {/* Content */}
            <div className="space-y-8 order-1 lg:order-2">
              <div className="space-y-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                    {React.createElement(slides[currentSlide].icon, { className: "h-6 w-6 text-primary" })}
                  </div>
                  <Badge variant="outline" className="text-sm">
                    {currentSlide + 1} / {slides.length}
                  </Badge>
                </div>
                
                <h3 className="text-3xl font-bold text-foreground">
                  {t(slides[currentSlide].titleKey)}
                </h3>
                
                <p className="text-lg text-muted-foreground leading-relaxed">
                  {t(slides[currentSlide].descriptionKey)}
                </p>
              </div>

              {/* Features */}
              <div className="space-y-3">
                {currentFeatures.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full"></div>
                    <span className="text-foreground">{feature}</span>
                  </div>
                ))}
              </div>

              {/* Slide indicators */}
              <div className="flex gap-2">
                {slides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    aria-label={`${t('productDemo.goToSlide')} ${index + 1}`}
                    className={`w-12 h-3 rounded-full transition-all duration-300 ${
                      index === currentSlide 
                        ? 'bg-primary' 
                        : 'bg-muted hover:bg-muted-foreground/20'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductDemoSection;
